/**
 * Ce que la table doit s'entendre dire.
 *
 * Le § 3.1 du plan liste, parmi les défauts de la v1, « aucun rythme narratif : le
 * sec, le rampeau et le 421 passent inaperçus ». C'est ce module qui les fait
 * exister : il regarde ce qui a changé entre deux états et en tire ce qu'il faut
 * annoncer, avec un **ton** que la scène traduit en lumière et en mouvement.
 *
 * ## Déduites de l'état, jamais d'un journal
 *
 * Le moteur produit bien des `Evenement`, mais le § 6.4 est formel : Colyseus ne
 * conserve que la dernière valeur d'une propriété par intervalle de patch, et le
 * handshake est sauté lors d'une reconnexion automatique. Un affichage bâti sur les
 * événements raterait précisément les moments qu'il devait fêter — et il les raterait
 * **en ligne seulement**, c'est-à-dire là où on le testerait le moins.
 *
 * On compare donc deux états. Même méthode que la demande de jet, même vertu : elle
 * sert le solo et le réseau sans que l'affichage sache lequel des deux il sert.
 *
 * ## Les identifiants
 *
 * Stables et dérivés du contenu, parce que l'état est repoussé plusieurs fois par
 * seconde : sans cela, la même annonce se relancerait à chaque patch et l'écran
 * bégaierait.
 */

import { evaluerMain, type Partie } from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";

/**
 * Le registre d'une annonce. La scène le lit pour savoir quoi faire de la lumière :
 * `fete` illumine, `sombre` baisse le tapis, `fort` claque, `info` glisse.
 */
export type Ton = "info" | "fort" | "fete" | "sombre";

/**
 * Ce dont il s'agit, indépendamment du texte.
 *
 * Le texte est fait pour être lu et peut changer à tout moment ; le motif, lui, est
 * ce sur quoi le bruitage se branche (`son/partition.ts`). Sans lui, il faudrait
 * reconnaître un 421 en lisant une phrase française — un son qui disparaît parce
 * qu'on a reformulé une annonce est un défaut qu'aucun test n'attrape.
 */
export type Motif =
  | "421" | "nenette" | "sec" | "rampeau" | "jetons" | "decharge" | "sauve" | "fin";

export interface Annonce {
  /** Dérivé du contenu : la même annonce garde le même identifiant. */
  readonly id: string;
  /** De quoi il s'agit. Stable, lisible par une machine. */
  readonly motif: Motif;
  readonly texte: string;
  readonly ton: Ton;
  /** Durée d'affichage, en secondes. */
  readonly duree: number;
}

/** Score de la nénette (221), la plus faible du jeu — et la plus commentée. */
const NENETTE = "221";

/**
 * Ce qu'il faut annoncer entre deux états.
 *
 * `monSiege` permet de **tutoyer** celui qui regarde. Sans lui, on lisait « Toi
 * ramasse 7 jetons » : un nom passe mal en sujet, et une annonce s'adresse à
 * quelqu'un. En hot-seat, où l'appareil sert plusieurs joueurs, on ne le passe pas
 * et tout le monde est nommé — c'est justement ce qu'on veut quand l'écran change
 * de main.
 */
export function annoncesEntre(
  avant: Partie, apres: Partie, textes: Textes, monSiege?: number,
): readonly Annonce[] {
  const sorties: Annonce[] = [];
  const marque = apres.revision;
  // Le nom du joueur, et un drapeau « c'est moi » : la deuxième personne se construit
  // différemment d'une langue à l'autre, et concaténer un pronom au texte marcherait
  // en français et nulle part ailleurs.
  const nomDe = (siege: number): string =>
    apres.joueurs[siege]?.nom ?? `#${siege}`;
  const cestMoi = (siege: number): boolean => siege === monSiege;

  // --- les jets qui viennent d'être posés ------------------------------------
  const manche = apres.manche;
  if (manche) {
    const posesAvant = new Set(Object.keys(avant.manche?.poses ?? {}));
    for (const [siege, pose] of Object.entries(manche.poses)) {
      if (posesAvant.has(siege)) continue;
      const place = Number(siege);
      const combinaison = evaluerMain(pose.main);

      if (combinaison.cle === "421") {
        sorties.push({
          id: `421-${marque}-${siege}`,
          motif: "421",
          texte: textes.annonce421(nomDe(place), cestMoi(place)),
          ton: "fete",
          duree: 2.8,
        });
      } else if (combinaison.cle === NENETTE) {
        sorties.push({
          id: `nenette-${marque}-${siege}`,
          motif: "nenette",
          texte: textes.annonceNenette(nomDe(place), cestMoi(place)),
          ton: "info",
          duree: 2.2,
        });
      }
    }

    // --- le sec : le seul choix qui engage toute la table --------------------
    if (avant.manche?.lancersMax === null && manche.lancersMax === 1) {
      sorties.push({
        id: `sec-${marque}`,
        motif: "sec",
        texte: textes.annonceSec,
        ton: "fort",
        duree: 2.6,
      });
    }

    // --- le rampeau : on départage -------------------------------------------
    const rampeau = manche.rampeau;
    if (rampeau && rampeau.jets !== avant.manche?.rampeau?.jets) {
      const noms = rampeau.participants.map(nomDe).join(" · ");
      const enJeu = rampeau.role === "PERDANT" ? textes.rampeauQuiRamasse
        : rampeau.role === "DONNEUR" ? textes.rampeauQuiDonne : textes.rampeauQuiRecoit;
      if (!avant.manche?.rampeau) {
        sorties.push({
          id: `rampeau-${marque}`,
          motif: "rampeau",
          texte: textes.annonceRampeau(noms, enJeu),
          ton: "sombre",
          duree: 2.8,
        });
      }
    }
  }

  /**
   * D'OÙ VIENT LA MISE. Le § 3.1 reproche à la v1 que « le joueur ne comprend pas
   * pourquoi il perd » : un joueur qui ramasse 14 jetons sans savoir pourquoi lit un
   * chiffre juste et incompréhensible. Une manche vaut la valeur du MEILLEUR jet,
   * doublée si la manche est sèche — un brelan d'as sec fait 14.
   *
   * ⚠️ ON N'ANNONCE QUE CE QU'ON PEUT PROUVER. La tentation est de nommer la main
   * gagnante ; c'est faux, et le navigateur l'a montré tout de suite (« 4 jetons
   * (554) », alors qu'une lecture en vaut 1). La transition qui résout la manche
   * part de l'état où le DERNIER joueur n'a pas encore posé : la meilleure main
   * lisible dans l'état d'avant n'est pas la gagnante. Restent deux faits solides,
   * et ce sont justement les deux qui déroutent :
   *
   *   - la manche était **sèche**, donc la mise a doublé — lisible avant que le
   *     dernier joueur agisse, donc fiable ;
   *   - le pot était **presque vide**, donc on n'a pris que ce qu'il restait, ce qui
   *     explique un 421 qui ne rapporte pas dix.
   */
  const doublee = avant.manche?.lancersMax === 1;
  const potVide = avant.masse > 0 && apres.masse === 0;

  // --- les jetons qui changent de main ---------------------------------------
  for (const joueur of apres.joueurs) {
    const precedent = avant.joueurs[joueur.siege];
    if (!precedent) continue;
    const delta = joueur.jetons - precedent.jetons;
    if (delta > 0) {
      sorties.push({
        id: `prend-${marque}-${joueur.siege}`,
        motif: "jetons",
        texte: textes.annonceRamasse(
          nomDe(joueur.siege), textes.jetons(delta), cestMoi(joueur.siege))
          + (doublee ? " " + textes.miseDoublee : "")
          + (potVide ? " " + textes.misePotVide : ""),
        ton: "info",
        duree: 2,
      });
    }
  }

  // --- la bascule de phase, l'erreur la plus chère du 421 ---------------------
  if (avant.phase === "CHARGE" && apres.phase === "DECHARGE") {
    sorties.push({
      id: `decharge-${marque}`,
      motif: "decharge",
      // Le mot « décharge » est dans la phrase, et pas seulement dans le bandeau
      // de phase : c'est le terme qu'on entend à table, et le joueur doit pouvoir
      // relier ce qu'il lit ici à ce qu'il lira ensuite en haut de l'écran.
      texte: textes.annonceDecharge,
      ton: "fort",
      duree: 4,
    });
  }

  // --- ceux qui se libèrent ---------------------------------------------------
  for (const siege of apres.classement) {
    if (avant.classement.includes(siege)) continue;
    sorties.push({
      id: `sauve-${marque}-${siege}`,
      motif: "sauve",
      texte: textes.annonceSauve(nomDe(siege), cestMoi(siege)),
      ton: "info",
      duree: 2.2,
    });
  }

  // --- la fin -----------------------------------------------------------------
  if (avant.perdant === null && apres.perdant !== null) {
    sorties.push({
      id: `fin-${marque}`,
      motif: "fin",
      texte: textes.annonceFin(nomDe(apres.perdant), cestMoi(apres.perdant)),
      ton: "fort",
      duree: 5,
    });
  }

  return sorties;
}
